'use strict'

const NewsletterPopup = (() => {
  const SUBSCRIBED_PREFIX = 'newsletter-popup-subscribed-'
  const DISMISSED_PREFIX = 'newsletter-popup-dismissed-'
  const BIRTHDAY_TAG = 'birthday'

  const keyFor = (prefix, el) => `${prefix}${el.dataset.id || 'default'}`

  const setFlag = (prefix, el) => {
    const days = parseInt(el.dataset.limitLifetime, 10) || 0
    const data = { value: true, expires: null }

    if (days > 0) {
      const expires = new Date()
      expires.setDate(expires.getDate() + days)
      data.expires = expires.getTime()
    }

    localStorage.setItem(keyFor(prefix, el), JSON.stringify(data))
  }

  const hasFlag = (prefix, el) => {
    const raw = localStorage.getItem(keyFor(prefix, el))
    if (!raw) return false

    try {
      const parsed = JSON.parse(raw)
      if (parsed.expires && Date.now() > parsed.expires) {
        localStorage.removeItem(keyFor(prefix, el))
        return false
      }
      return parsed.value === true
    } catch {
      return false
    }
  }

  const markSubscribed = el => setFlag(SUBSCRIBED_PREFIX, el)
  const markDismissed = el => setFlag(DISMISSED_PREFIX, el)
  const isSubscribed = el => hasFlag(SUBSCRIBED_PREFIX, el)
  const isDismissed = el => hasFlag(DISMISSED_PREFIX, el)

  // Show the popup right away (used after a no-JS success reload) so the
  // confirmation message is visible, reusing the engine's slide-in animation.
  const openNow = el => {
    if (el.classList.contains('popup-open')) return
    el.classList.add('popup-open')

    const wrap = el.querySelector('.popup-wrap')
    if (!wrap) return

    wrap.classList.add('popup-animated', 'popupOpenSlide')
    wrap.addEventListener(
      'animationend',
      () => wrap.classList.remove('popup-animated', 'popupOpenSlide'),
      { once: true }
    )
  }

  // Persist a "dismissed" flag the moment the popup goes from open to closed.
  // No auto-close trigger is configured, so an open→closed transition can only
  // be the user closing it (X button, overlay click or ESC key). Merely showing
  // the popup never sets a flag, so it reappears until the user acts on it.
  const watchDismiss = el => {
    let wasOpen = el.classList.contains('popup-open')

    const observer = new MutationObserver(() => {
      const isOpen = el.classList.contains('popup-open')
      if (wasOpen && !isOpen && !isSubscribed(el)) markDismissed(el)
      wasOpen = isOpen
    })

    observer.observe(el, { attributes: true, attributeFilter: ['class'] })
  }

  // Auto-format the birthday input as DD/MM/AAAA while typing.
  const applyDateMask = input => {
    input.addEventListener('input', () => {
      const digits = input.value.replace(/\D/g, '').slice(0, 8)
      const parts = []

      if (digits.length > 0) parts.push(digits.slice(0, 2))
      if (digits.length > 2) parts.push(digits.slice(2, 4))
      if (digits.length > 4) parts.push(digits.slice(4, 8))

      input.value = parts.join('/')
    })
  }

  // Merge the birthday into the customer tags as "birthday: dd/mm/aaaa".
  // The customer form's note field is not reliably persisted, so a tag is used.
  const composeTags = form => {
    const tagsInput = form.querySelector('[name="contact[tags]"]')
    const birthday = form.querySelector('.newsletter-popup__birthday')
    if (!tagsInput) return

    if (tagsInput.dataset.baseTags === undefined) {
      tagsInput.dataset.baseTags = tagsInput.value
    }

    const tags = tagsInput.dataset.baseTags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag && !tag.startsWith(`${BIRTHDAY_TAG}:`))

    const value = birthday?.value.trim()
    if (value) tags.push(`${BIRTHDAY_TAG}: ${value}`)

    tagsInput.value = tags.join(', ')
  }

  const showMessage = (form, text, modifier) => {
    form.querySelector('.newsletter-popup__message')?.remove()

    const tag = modifier === 'success' ? 'h3' : 'small'
    const message = document.createElement(tag)
    message.className = `newsletter-popup__message newsletter-popup__message--${modifier} form__message`
    message.setAttribute('tabindex', '-1')
    message.textContent = text
    form.querySelector('.newsletter-popup__button').before(message)
    message.focus()
  }

  const showSuccess = (el, form, text) => {
    form
      .querySelectorAll('.newsletter-popup__field, .newsletter-popup__button')
      .forEach(node => node.remove())
    showMessage(form, text, 'success')
    markSubscribed(el)
  }

  // Submit the customer form without a full page reload: a native submit lands
  // on a jarring whole-page refresh, so we POST it in the background instead.
  // Shopify redirects to ...?customer_posted=true on success.
  const handleSubmit = (el, form) => {
    form.addEventListener('submit', async event => {
      event.preventDefault()
      composeTags(form)

      const button = form.querySelector('.newsletter-popup__button')
      button.setAttribute('aria-busy', 'true')
      button.disabled = true

      try {
        const response = await fetch(form.action, {
          method: 'POST',
          body: new URLSearchParams(new FormData(form)),
          headers: { Accept: 'text/html' }
        })

        const succeeded =
          response.ok &&
          new URL(response.url).searchParams.get('customer_posted') === 'true'

        if (succeeded) {
          showSuccess(el, form, el.dataset.successText || '')
        } else {
          showMessage(form, el.dataset.errorText || '', 'error')
        }
      } catch {
        showMessage(form, el.dataset.errorText || '', 'error')
      } finally {
        button.removeAttribute('aria-busy')
        button.disabled = false
      }
    })
  }

  const setupPopup = el => {
    if (el.dataset.npReady) return
    el.dataset.npReady = 'true'

    // No-JS fallback: a native submit reloaded the page with the success block.
    if (el.querySelector('.newsletter-popup__message--success')) {
      markSubscribed(el)
      openNow(el)
      return
    }

    // Completed or closed on a previous visit → block the engine from opening.
    if (isSubscribed(el) || isDismissed(el)) {
      el.classList.add('popup-already-opened')
      return
    }

    watchDismiss(el)

    const birthdayInput = el.querySelector('.newsletter-popup__birthday')
    const form = el.querySelector('.newsletter-popup__form')

    if (birthdayInput) applyDateMask(birthdayInput)
    if (form) handleSubmit(el, form)
  }

  const init = () => {
    document.querySelectorAll('[data-newsletter-popup]').forEach(setupPopup)
  }

  return { init }
})()

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', NewsletterPopup.init)
} else {
  NewsletterPopup.init()
}

document.addEventListener('shopify:section:load', NewsletterPopup.init)
